import type { ReactNode } from 'react';
import { ScreenDialog } from '@voll-intelligence/ui-kit/ui/screen-dialog';
import type { FlightExplorerRequest } from '../explorer/search_source.js';
import type { FlightOfferSource } from '../results/flight_offer_source.js';
import type { RenderFlightDetail } from '../results/offer/detail_layer.js';
import type { TariffFetchState } from './fare_expansion.js';
import type { FareSel } from './fare_selection.js';
import type { FaresPageFlight } from './fares_page_shared.js';
import {
  FlightRequestFaresPage,
  type FaresRequestText,
  type FlightAirportSwitchFn,
} from './fares_request_page.js';

/**
 * **A tela em diálogo da seleção de tarifa no celular (#3875).** Abaixo de
 * `sm` a escada não cabe embaixo do card — o trilho de famílias cortava o
 * segundo card e a seta cobria o primeiro — então a página "Escolha as
 * tarifas" abre por cima do que estiver em cena, com o desenho do celular
 * (`FlightFaresPage` → `FaresPageStack`). UMA casca para todos os lugares em
 * que a tarifa se escolhe num celular (padronização de 30/09): o card do chat,
 * a perna do roteiro e o explorer da bancada e do "Ver todos".
 *
 * É a `ScreenDialog`: portal (o card vive dentro de um carrossel com
 * `overflow`, e um `fixed` ali seria cortado), modal, Esc e o foco de volta a
 * quem abriu são do Base UI. Aqui o Esc PODE fechar — fechar não perde busca
 * nenhuma: no chat a seleção fica no `useOfferFares` do host e o card segue na
 * lista; no explorer fechar é o "Alterar voo" da perna, que devolve a lista
 * dela. A gaveta de detalhe e assentos monta DENTRO da tela (`useLayerHost`), e
 * o Esc com ela aberta é dela.
 */
export function FlightFaresDialog({
  label,
  onClose,
  children,
}: Readonly<{
  /** O nome acessível da tela (o título curto da página de tarifas). */
  label: string;
  onClose: () => void;
  /** A `FlightFaresPage` (com `onBack={onClose}`) e o que o host quiser por
   *  cima dela — o aviso de confirm travado do explorer. */
  children: ReactNode;
}>) {
  return (
    // A grade de uma linha dá altura DEFINIDA à página (`.ffs` tem
    // `min-height: 100%`), que é o que empurra a faixa do CTA para o pé quando
    // a lista é curta. O `pt` soma a status bar do iPhone (viewport-fit=cover
    // no WebView), como a barra das telas em diálogo.
    <ScreenDialog
      label={label}
      onClose={onClose}
      className="scrollbar-rail pr-[calc(1rem-var(--scrollbar-rail))] pl-4 grid grid-rows-[minmax(0,1fr)] overflow-auto overscroll-contain pt-[calc(12px+env(safe-area-inset-top,0px))]"
    >
      {children}
    </ScreenDialog>
  );
}

/**
 * A casca acima montada pelo CARD de oferta: a `FlightFaresPage` da PRÓPRIA
 * oferta (`offer`), no lugar em que o card empilhado aparece — o card do chat,
 * a gaveta "ver todos" e a perna do roteiro. "Voltar" e "Alterar voo" são a
 * mesma saída: o voo não é um estado desta casca, é o card de onde ela abriu.
 *
 * O pedido que a página escreve no subtítulo sai da PRÓPRIA oferta
 * (`requestOf`): a lista compartilhada não conhece o pedido do host, e cabine e
 * contagem de viajantes, que só o explorer sabe, ficam de fora — no celular o
 * subtítulo diz a rota e a forma, que é o que a oferta carrega.
 */
export function OfferFaresDialog({
  offer,
  state,
  sel,
  readOnly,
  cta,
  text,
  airportSwitchOf,
  renderDetail,
  onPick,
  onSelect,
  onRetry,
  onResearch,
  onClose,
}: Readonly<{
  offer: FlightOfferSource;
  state: TariffFetchState;
  sel: FareSel;
  readOnly: boolean | undefined;
  /** "Próximo" no chat, "Ir para checkout" no explorer — ver
   *  `FlightFaresPageProps.ctaLabel`. `disabled` é o draftLock do explorer
   *  (faixa com edição não buscada), como em `FareExpansion.cta`. */
  cta: { label: string; disabled: boolean };
  text: FaresRequestText;
  airportSwitchOf: FlightAirportSwitchFn;
  renderDetail: RenderFlightDetail;
  onPick: (patch: Partial<FareSel>) => void;
  onSelect: (picked: { total: number; currency: string; names: string }) => void;
  onRetry: () => void;
  onResearch: (() => void) | undefined;
  onClose: () => void;
}>) {
  const request = requestOf(offer);
  const inbound = offer.returnLeg ? flightOf(offer.offerId, offer.returnLeg) : undefined;
  return (
    <FlightFaresDialog label={text.copy.titleShort} onClose={onClose}>
      <FlightRequestFaresPage
        request={request}
        text={text}
        airportSwitchOf={airportSwitchOf}
        renderDetail={renderDetail}
        outbound={flightOf(offer.offerId, offer)}
        inbound={inbound}
        state={state}
        sel={sel}
        {...(readOnly ? { readOnly } : {})}
        ctaLabel={cta.label}
        ctaDisabled={cta.disabled}
        onPick={onPick}
        // "Alterar voo" aqui é voltar à lista: o voo não é um estado desta
        // casca, é o card de onde ela abriu.
        onChangeFlight={onClose}
        onCheckout={onSelect}
        onRetry={onRetry}
        onResearch={onResearch}
        onBack={onClose}
      />
    </FlightFaresDialog>
  );
}

/** A perna como a página a desenha: o primeiro trecho e o total de escalas
 *  (mesma leitura do card empilhado — `stops` do provider, senão derivado). */
function flightOf(
  offerId: string,
  leg: Pick<FlightOfferSource, 'segments' | 'stops'>,
): FaresPageFlight {
  const seg = leg.segments[0];
  if (!seg) throw new Error('fares_dialog: oferta sem trecho');
  return { offerId, seg, stops: leg.stops ?? leg.segments.length - 1 };
}

/** O pedido que a oferta IMPLICA — rota de ponta a ponta, o dia da saída e a
 *  forma (a volta existe ⇒ ida e volta). Só o que a oferta carrega. */
function requestOf(offer: FlightOfferSource): FlightExplorerRequest {
  const first = offer.segments[0];
  const last = offer.segments.at(-1);
  const back = offer.returnLeg?.segments[0];
  return {
    mode: back ? 'round_trip' : 'one_way',
    legs: [
      {
        origin: first?.from ?? '',
        destination: last?.to ?? '',
        departDate: first?.departAt.slice(0, 10) ?? '',
      },
    ],
    ...(back ? { returnDate: back.departAt.slice(0, 10) } : {}),
  };
}
